import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { User } from 'interfaces/user';
import { RootState } from 'store';

export interface AuthState {
  user: User | null;
}

const initialState: AuthState = {
  user: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * @description Update the user state with new user information
     *
     * @SADtrace SAD-003-1, SAD-003-2.1
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @param {AuthState} state - Current auth state
     * @param {PayloadAction<AuthState>} action - Action containing updated user data
     *
     * @returns {void}
     */
    updateUser: (state, action: PayloadAction<AuthState>) => {
      state.user = action.payload.user;
    },
    /**
     * @description Reset the auth state to initial values, clearing user data
     *
     * @SADtrace SAD-003-1, SAD-003-1.1
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @returns {AuthState} Initial auth state
     */
    reset: () => ({ ...initialState }),
  },
});

/**
 * @description Redux selector that retrieves the current user from the auth state
 *
 * @SADtrace SAD-003-1, SAD-003-2.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {RootState} state - Redux root state
 *
 * @returns {User | null} Current user or null if not authenticated
 */
export const selectUser = (state: RootState): User | null => state.auth.user;

export const authActions = authSlice.actions;

export default authSlice.reducer;
